import GlassCard from '../ui/glass/components/GlassCard';

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { Box, Typography, Grid, TextField, Button, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import PageHeader from '../components/common/PageHeader';
import { Loading } from '../components/common/Loading';

const MarkaModelYonetimi = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newBrandName, setNewBrandName] = useState('');
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [newModelName, setNewModelName] = useState('');

    // Dialog states
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [editBrandName, setEditBrandName] = useState('');

    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, "brands"));
            const brandsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Sort alphabetically
            brandsData.sort((a, b) => a.name.localeCompare(b.name));
            setBrands(brandsData);
        } catch (error) {
            console.error("Markalar yüklenirken hata:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddBrand = async () => {
        if (!newBrandName.trim()) return;
        try {
            await addDoc(collection(db, "brands"), {
                name: newBrandName.trim(),
                models: []
            });
            setNewBrandName('');
            fetchBrands();
        } catch (error) {
            console.error("Marka eklenirken hata:", error);
        }
    };

    const handleDeleteBrand = async (brandId) => {
        if (!window.confirm("Bu markayı silmek istediğinizden emin misiniz?")) return;
        try {
            await deleteDoc(doc(db, "brands", brandId));
            if (selectedBrand?.id === brandId) setSelectedBrand(null);
            fetchBrands();
        } catch (error) {
            console.error("Marka silinirken hata:", error);
        }
    };

    const handleEditBrand = (brand) => {
        setEditingBrand(brand);
        setEditBrandName(brand.name);
        setEditDialogOpen(true);
    };

    const saveBrandName = async () => {
        if (!editingBrand || !editBrandName.trim()) return;
        try {
            const brandRef = doc(db, "brands", editingBrand.id);
            await updateDoc(brandRef, { name: editBrandName.trim() });
            setEditDialogOpen(false);
            fetchBrands();
        } catch (e) {
            console.error("Marka güncellenirken hata:", e);
        }
    };

    const handleAddModel = async () => {
        if (!selectedBrand || !newModelName.trim()) return;
        try {
            const brandRef = doc(db, "brands", selectedBrand.id);
            await updateDoc(brandRef, {
                models: arrayUnion({ model: newModelName.trim() }) // Object array structure kept for compatibility
            });
            setNewModelName('');
            // Update local state without refetching all
            const updatedBrand = { ...selectedBrand, models: [...(selectedBrand.models || []), { model: newModelName.trim() }] };
            setSelectedBrand(updatedBrand);
            setBrands(prev => prev.map(b => b.id === updatedBrand.id ? updatedBrand : b));
        } catch (error) {
            console.error("Model eklenirken hata:", error);
        }
    };



    const handleDeleteModel = async (modelToDelete) => {
        if (!window.confirm("Bu modeli silmek istediğinizden emin misiniz?")) return;
        try {
            const brandRef = doc(db, "brands", selectedBrand.id);
            await updateDoc(brandRef, {
                models: arrayRemove(modelToDelete)
            });

            const updatedBrand = {
                ...selectedBrand,
                models: selectedBrand.models.filter(m => m.model !== modelToDelete.model)
            };
            setSelectedBrand(updatedBrand);
            setBrands(prev => prev.map(b => b.id === updatedBrand.id ? updatedBrand : b));
        } catch (error) {
            console.error("Model silinirken hata:", error);
        }
    };

    if (loading) return <Loading message="Markalar yükleniyor..." />;

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="Marka & Model Yönetimi"
                subtitle="Servis kayıtları ve cihaz formlarında kullanılacak marka ve model tanımları."
                icon={<CategoryIcon />}
            />

            <Grid container spacing={3}>
                {/* Sol Taraf: Markalar */}
                <Grid item xs={12} md={5}>
                    <GlassCard sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Markalar</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Yeni Marka Adı"
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                            />
                            <Button variant="contained" onClick={handleAddBrand} startIcon={<AddIcon />}>Ekle</Button>
                        </Box>

                        <Divider />
                        <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                            {brands.map(brand => (
                                <ListItem
                                    key={brand.id}
                                    button
                                    selected={selectedBrand?.id === brand.id}
                                    onClick={() => setSelectedBrand(brand)}
                                >
                                    <ListItemText primary={brand.name} secondary={`${brand.models?.length || 0} Model`} />
                                    <ListItemSecondaryAction>
                                        <IconButton aria-label="Markayı düzenle" size="small" onClick={() => handleEditBrand(brand)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton aria-label="Markayı sil" size="small" onClick={() => handleDeleteBrand(brand.id)}><DeleteIcon fontSize="small" /></IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                            {brands.length === 0 && <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>Henüz marka eklenmemiş.</Typography>}
                        </List>
                    </GlassCard>
                </Grid>

                {/* Sağ Taraf: Modeller */}
                <Grid item xs={12} md={7}>
                    <GlassCard sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            {selectedBrand ? `${selectedBrand.name} - Modelleri` : 'Modelleri Yönetmek İçin Marka Seçin'}
                        </Typography>

                        {selectedBrand ? (
                            <>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Yeni Model Adı"
                                        value={newModelName}
                                        onChange={(e) => setNewModelName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddModel()}
                                    />
                                    <Button variant="contained" onClick={handleAddModel} startIcon={<AddIcon />}>Ekle</Button>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedBrand.models?.map((modelObj, index) => (
                                        <Chip
                                            key={index}
                                            label={modelObj.model}
                                            onDelete={() => handleDeleteModel(modelObj)}
                                        />
                                    ))}
                                    {(!selectedBrand.models || selectedBrand.models.length === 0) && (
                                        <Typography color="text.secondary">Bu markaya ait model bulunmuyor.</Typography>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: 'text.secondary' }}>
                                <Typography>← Soldan bir marka seçin</Typography>
                            </Box>
                        )}
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>Marka Düzenle</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Marka Adı"
                        fullWidth
                        variant="outlined"
                        value={editBrandName}
                        onChange={(e) => setEditBrandName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
                    <Button onClick={saveBrandName} variant="contained">Kaydet</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MarkaModelYonetimi;
